import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileConfigService } from '../services/mobileConfigService';
import { mobileAuthService } from '../services/mobileAuthService';
import { mobilePixRenkeiService } from '../services/mobilePixRenkeiService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';
import Footer from '../components/Footer';
import PixRenkeiModal from '../components/PixRenkeiModal';

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  header: {
    textAlign: 'center',
    padding: '20px 0px 5px'
  },
  logo: {
    display: 'block',
    margin: '0 auto 10px',
    maxWidth: '250px',
    width: '100%',
    height: 'auto'
  },
  versionInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 20px',
    marginBottom: '20px'
  },
  version: {
    fontSize: '10pt',
    fontWeight: 'bold',
    paddingLeft: '10px',
    letterSpacing: '2px',
    color: '#333'
  },
  userId: {
    fontSize: '8pt',
    textAlign: 'right',
    paddingRight: '10px',
    letterSpacing: '2px',
    color: '#333'
  },
  settingsSection: {
    padding: '0 20px',
    marginBottom: '30px'
  },
  settingLabel: {
    width: '100%',
    background: '#EEE',
    padding: '3px 12px',
    color: '#999',
    fontWeight: 'bold',
    fontSize: '12pt',
    marginBottom: '2px',
    borderRadius: '4px 4px 0 0'
  },
  settingSelect: {
    width: '100%',
    fontSize: '12pt',
    border: '1px solid #CCC',
    borderTop: 'none',
    padding: '6px',
    borderRadius: '0 0 4px 4px',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    paddingRight: '32px',
    boxSizing: 'border-box'
  },
  actionButtons: {
    padding: '0 20px',
    marginBottom: '20px'
  },
  actionButton: {
    height: '32px',
    width: '90%',
    margin: '12px auto',
    background: '#2B2222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    letterSpacing: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

const AppConfig = () => {
  const navigate = useNavigate();
  
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError();
  
  // 設定データ
  const [config, setConfig] = useState({
    uploadConfig: '0', // 0: Wifiのみ, 1: 常にアップロード
    isSaveLocal: '1'   // 0: 保存しない, 1: 保存する
  });
  
  // ユーザー情報データ
  const [userInfo, setUserInfo] = useState({
    version: '1.0.2', // アプリバージョン
    serialNo: '', // シリアル番号
    companyName: '' // pixRenkei企業名
  });
  
  
  // pix連携関連の状態
  const [pixRenkeiData, setPixRenkeiData] = useState(null); // pix連携データ
  const [showPixRenkeiModal, setShowPixRenkeiModal] = useState(false); // pix連携モーダル表示フラグ
  const [isPixRenkeiProcessing, setIsPixRenkeiProcessing] = useState(false); // pix連携処理中フラグ

  // コンポーネント初期化
  useEffect(() => {
    loadConfig();
    loadPixRenkei();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 設定情報の取得
  const loadConfig = useCallback(async () => {
    try {
      const result = await mobileConfigService.getConfig();

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '設定情報の取得に失敗しました。', null, 'error');
      }

      // 撮影画像ローカル保存を設定
      if(result.isSaveLocal !== null){
        setConfig(prev => ({
          ...prev,
          isSaveLocal: result.isSaveLocal
        }));
      }
      
      // シリアル番号を設定
      if(result.serialNo !== null){
        setUserInfo(prev => ({
          ...prev,
          serialNo: result.serialNo
        }));
      }
      
      // 企業名を設定
      if(result.companyName !== null && result.companyName !== undefined){
        setUserInfo(prev => ({
          ...prev,
          companyName: result.companyName
        }));
      }
      
      // アップロード方式を取得
      await loadUploadConfig();
    } catch (error) {
      handleApiError(new Error('設定情報の取得に失敗しました。'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // アップロード方式の取得
  const loadUploadConfig = useCallback(async () => {
    try {
      const result = await mobileConfigService.getUploadConfig();

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', 'アップロード方式の取得に失敗しました。', null, 'error');
      }
      
      // アップロード方式の値を設定
      if(result.uploadConfig !== null){
        setConfig(prev => ({
          ...prev,
          uploadConfig: result.uploadConfig
        }));
      } 
    } catch (error) {
      handleApiError(new Error('アップロード方式の取得に失敗しました。'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pix連携情報の取得
  const loadPixRenkei = useCallback(async () => {
    try {
      const result = await mobilePixRenkeiService.getPixRenkei();
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'pix連携情報の取得に失敗しました。', null, 'error');
        return;
      }
      
      if (result.renkeiCount !== 0 && result.requestFlg === 0) {
        setPixRenkeiData(result);
      } else {
        setPixRenkeiData(null);
      }
    } catch (error) {
      // エラーが発生しても画面表示は継続
      handleApiError(new Error('pix連携情報の取得に失敗しました。'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pix連携の承認処理
  const handlePixRenkeiApprove = async () => {
    if (!pixRenkeiData) return;
    
    setIsPixRenkeiProcessing(true);
    try {
      const updateData = {
        prePixId: pixRenkeiData.prePixId,
        pixUserId: pixRenkeiData.pixUserId,
        requestFlg: '1' // 承認済み
      };
      
      const result = await mobilePixRenkeiService.updatePixRenkei(updateData);
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'pix連携の承認に失敗しました。', null, 'error');
        setIsPixRenkeiProcessing(false);
        return;
      }
      
      // 承認完了後の処理
      setShowPixRenkeiModal(false);
      setPixRenkeiData(null);
      
      // 成功メッセージを表示
      MessageDialog.show('メッセージ', '承認を許可しました。\n追加されていることをログインまたは「ユーザー管理」よりご確認ください。\npix-labelの再ログインをお願い致します。', null, 'success');
      
      // ログイン画面に遷移
      navigate('/appLogin');
      
    } catch (error) {
      handleApiError(new Error('pix連携の承認に失敗しました。'));
    } finally {
      setIsPixRenkeiProcessing(false);
    }
  };

  // pix連携の拒否処理
  const handlePixRenkeiReject = async () => {
    if (!pixRenkeiData) return;
    
    setIsPixRenkeiProcessing(true);
    try {
      const updateData = {
        prePixId: pixRenkeiData.prePixId,
        pixUserId: pixRenkeiData.pixUserId,
        requestFlg: '2' // 承認拒否
      };
      
      const result = await mobilePixRenkeiService.updatePixRenkei(updateData);
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'pix連携の拒否に失敗しました。', null, 'error');
        setIsPixRenkeiProcessing(false);
        return;
      }
      
      // 拒否完了後の処理
      setShowPixRenkeiModal(false);
      setPixRenkeiData(null);
      
      // 成功メッセージを表示
      MessageDialog.show('メッセージ', '承認を拒否しました。', null, 'success');
      
      // 再読み込み
      await loadPixRenkei();
      
    } catch (error) {
      handleApiError(new Error('pix連携の拒否に失敗しました。'));
    } finally {
      setIsPixRenkeiProcessing(false);
    }
  };

  // 設定変更処理
  const handleUploadConfigChange = async (value) => {
    setConfig(prev => ({ ...prev, uploadConfig: value }));
    try {
      const result = await mobileConfigService.updateUploadConfig(value);

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', 'アップロード方式の更新に失敗しました。', null, 'error');
        return;
      }

      // ネイティブアプリに送信モード変更を通知
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.changeSettingSendMode) {
        window.webkit.messageHandlers.changeSettingSendMode.postMessage(value)
      } else if (window.changeSettingSendMode) {
        window.changeSettingSendMode.postMessage(value)
      }

    } catch (error) {
      handleApiError(new Error('アップロード方式の更新に失敗しました。'));
    }
  };

  const handleSaveLocalChange = async (value) => {
    setConfig(prev => ({ ...prev, isSaveLocal: value }));
    try {
      const result = await mobileConfigService.updateSaveLocal(value);

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '撮影画像ローカル保存の更新に失敗しました。', null, 'error');
        return;
      }

    } catch (error) {
      handleApiError(new Error('撮影画像ローカル保存の更新に失敗しました。'));
    }
  };

  // ナビゲーション処理
  const handleInquiry = () => {
    navigate('/appInquiry');
  };

  const handleUserInfo = () => {
    navigate('/appUserInfo');
  };

  const handleLogout = async () => {
    try {
      await mobileAuthService.logout();
      // ネイティブアプリのUUIDをクリア
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.registerUserID) {
        window.webkit.messageHandlers.registerUserID.postMessage('')
      } else if (window.registerUserID) {
        window.registerUserID.postMessage('')
      }
      // localStorageのネイティブ連携情報もクリア
      localStorage.removeItem('nativeUuid')
      localStorage.removeItem('nativeDeviceId')
      navigate('/appLogin');
    } catch (error) {
      handleApiError(new Error('ログアウトに失敗しました。'));
    }
  };

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <img 
          src="/core/img/pix-label-logo.png" 
          alt="pix-label" 
          style={styles.logo}
        />
        <div style={styles.versionInfo}>
          <span style={styles.version}>version {userInfo.version}</span>
          <span style={styles.userId}>ID:{userInfo.serialNo}</span>
        </div>
      </div>

      {/* pix連携招待通知 */}
      {pixRenkeiData && (
        <div style={{
          padding: '5px 20px',
          margin: '0 5px 5px 20px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '14px',
            color: '#FF0000',
            fontWeight: 'bold',
            marginBottom: '10px'
          }}>
            ※pix-storageから招待がきています
          </div>

                     <button 
             style={{
               ...styles.actionButton,
               backgroundColor: '#FF6B35',
               height: '32px',
               fontSize: '14px',
               letterSpacing: '2px'
             }}
             onClick={() => setShowPixRenkeiModal(true)}
             onMouseEnter={(e) => {
               e.target.style.background = '#FF8A5C';
             }}
             onMouseLeave={(e) => {
               e.target.style.background = '#FF6B35';
             }}
           >
             招待承認
           </button>
        </div>
      )}

      {/* 設定セクション */}
      <div style={styles.settingsSection}>
        <div style={{ marginBottom: '12px' }}>
          <div style={styles.settingLabel}>アップロード方式</div>
          <select 
            id="uploadConfig"
            value={config.uploadConfig}
            onChange={(e) => handleUploadConfigChange(e.target.value)}
            style={styles.settingSelect}
          >
            <option value="0">Wifiに接続されている時のみアップロード</option>
            <option value="1">常にアップロード</option>
          </select>
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <div style={styles.settingLabel}>撮影画像ローカル保存</div>
          <select 
            id="saveLocal"
            value={config.isSaveLocal}
            onChange={(e) => handleSaveLocalChange(e.target.value)}
            style={styles.settingSelect}
          >
            <option value="0">保存しない</option>
            <option value="1">保存する</option>
          </select>
        </div>
      </div>

      {/* アクションボタン */}
      <div style={styles.actionButtons}>
        <button 
          style={styles.actionButton}
          onClick={handleInquiry}
          onMouseEnter={(e) => {
            e.target.style.background = '#4a4a4a';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#2B2222';
          }}
        >
          お問い合わせ
        </button>
        
        <button 
          style={styles.actionButton}
          onClick={handleUserInfo}
          onMouseEnter={(e) => {
            e.target.style.background = '#4a4a4a';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#2B2222';
          }}
        >
          ユーザー情報
        </button>
        
        <button 
          style={styles.actionButton}
          onClick={handleLogout}
          onMouseEnter={(e) => {
            e.target.style.background = '#4a4a4a';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#2B2222';
          }}
        >
          ログアウトする
        </button>
      </div>

      {/* ボトムナビゲーション */}
      <Footer />

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />

      {/* Pix連携承認モーダル */}
      <PixRenkeiModal
        isOpen={showPixRenkeiModal}
        onClose={() => setShowPixRenkeiModal(false)}
        onApprove={handlePixRenkeiApprove}
        onReject={handlePixRenkeiReject}
        isProcessing={isPixRenkeiProcessing}
        companyName={userInfo.companyName || ''}
      />
    </div>
  );
};

export default AppConfig;

